import { useMemo, useState } from "react";
import { Flame, Search } from "lucide-react";
import { useFirestoreCollection } from "@/hooks/useFirestoreCollection";
import { toArray } from "@/lib/format";
import LiveEventCard from "@/components/LiveEventCard";
import ConnectionError from "@/components/ConnectionError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function EventsList() {
  const [retryKey, setRetryKey] = useState(0);
  const [filter, setFilter] = useState("all"); // all | live | closed
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");

  const { data: events, loading, error } = useFirestoreCollection("eventos", {
    key: retryKey,
  });
  const { data: torneos } = useFirestoreCollection("torneos", {
    key: retryKey,
  });

  // Map eventId -> set of all MC names across every round (incl. eliminated)
  const namesByEventId = useMemo(() => {
    const map = {};
    (torneos || []).forEach((t) => {
      const key = t.eventId || t.eventoId;
      if (!key) return;
      if (!map[key]) map[key] = new Set();
      toArray(t.listaNombres).forEach((n) =>
        map[key].add(typeof n === "string" ? n : n?.nombre || JSON.stringify(n))
      );
    });
    return map;
  }, [torneos]);

  const getParticipantCount = (ev) => {
    const names = namesByEventId[ev.id];
    if (names && names.size > 0) return names.size;
    return toArray(ev.participantes || ev.listaNombres).length;
  };

  const sorted = useMemo(() => {
    const arr = [...(events || [])];
    arr.sort((a, b) => {
      const la = a.isClosed ? 1 : 0;
      const lb = b.isClosed ? 1 : 0;
      if (la !== lb) return la - lb;
      return (b.fecha?.seconds || 0) - (a.fecha?.seconds || 0);
    });
    return arr;
  }, [events]);

  const cities = useMemo(() => {
    const set = new Set();
    sorted.forEach((e) => {
      const c = e.city || e.ciudad || e.lugar;
      if (c && typeof c === "string") set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sorted]);

  const countries = useMemo(() => {
    const set = new Set();
    sorted.forEach((e) => {
      const c = e.country || e.pais;
      if (c && typeof c === "string") set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sorted]);

  const filtered = useMemo(() => {
    return sorted.filter((e) => {
      if (filter === "live" && e.isClosed) return false;
      if (filter === "closed" && !e.isClosed) return false;
      const eCity = e.city || e.ciudad || e.lugar;
      const eCountry = e.country || e.pais;
      if (cityFilter !== "all" && eCity !== cityFilter) return false;
      if (countryFilter !== "all" && eCountry !== countryFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          String(e.titulo || "").toLowerCase().includes(s) ||
          String(eCity || "").toLowerCase().includes(s) ||
          String(eCountry || "").toLowerCase().includes(s) ||
          String(e.modalidad || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [sorted, filter, search, cityFilter, countryFilter]);

  const liveCount = sorted.filter((e) => !e.isClosed).length;
  const closedCount = sorted.length - liveCount;

  const FilterBtn = ({ value, label, count }) => (
    <button
      onClick={() => setFilter(value)}
      data-testid={`filter-${value}`}
      className={`inline-flex items-center gap-2 border px-4 py-2 font-ui text-[10px] font-bold uppercase tracking-[0.2em] ${
        filter === value
          ? "border-[#FF5500] bg-[#FF5500] text-white"
          : "border-[#1E293B] bg-[#0F172A] text-[#94A3B8] hover:border-[#FF5500] hover:text-white"
      }`}
    >
      {label}
      <span className={filter === value ? "text-white" : "text-[#FF5500]"}>
        {count}
      </span>
    </button>
  );

  const activeExtraFilters =
    (cityFilter !== "all" ? 1 : 0) + (countryFilter !== "all" ? 1 : 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
      {/* Hero */}
      <section className="mb-12 flex flex-col gap-6 border-b border-[#1E293B] pb-10">
        <div className="flex items-center gap-3">
          <span className="h-px w-10 bg-[#FF5500]" />
          <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF5500]">
            Dashboard · Freestyle Battles
          </span>
        </div>
        <h1
          data-testid="page-title"
          className="font-display text-5xl leading-none tracking-wider text-white sm:text-6xl lg:text-7xl"
        >
          Arena en vivo.<br />
          <span className="text-[#FF5500]">Rimas sin filtro.</span>
        </h1>
        <p className="max-w-2xl font-body text-base text-[#94A3B8]">
          Sigue cada batalla, bracket y voto en tiempo real. Todos los
          torneos activos y pasados generados desde la app La Pinza.
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <FilterBtn value="all" label="Todos" count={sorted.length} />
          <FilterBtn value="live" label="En vivo" count={liveCount} />
          <FilterBtn value="closed" label="Finalizados" count={closedCount} />
          <div className="ml-auto flex items-center gap-2 border border-[#1E293B] bg-[#0F172A] px-3 py-2">
            <Search className="h-3.5 w-3.5 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar torneo, ciudad, modalidad..."
              data-testid="search-input"
              className="w-64 bg-transparent font-body text-sm text-white placeholder:text-[#94A3B8] focus:outline-none"
            />
          </div>
        </div>

        {/* Location filters */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
            Ubicación
          </span>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger
              data-testid="country-filter"
              className="w-[180px] border-[#1E293B] bg-[#0F172A] font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-white"
            >
              <SelectValue placeholder="País" />
            </SelectTrigger>
            <SelectContent className="border-[#1E293B] bg-[#0F172A] text-white">
              <SelectItem
                value="all"
                data-testid="country-option-all"
                className="font-ui text-xs"
              >
                Todos los países
              </SelectItem>
              {countries.map((c) => (
                <SelectItem
                  key={c}
                  value={c}
                  data-testid={`country-option-${c}`}
                  className="font-ui text-xs"
                >
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger
              data-testid="city-filter"
              className="w-[200px] border-[#1E293B] bg-[#0F172A] font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-white"
            >
              <SelectValue placeholder="Ciudad" />
            </SelectTrigger>
            <SelectContent className="border-[#1E293B] bg-[#0F172A] text-white">
              <SelectItem
                value="all"
                data-testid="city-option-all"
                className="font-ui text-xs"
              >
                Todas las ciudades
              </SelectItem>
              {cities.map((c) => (
                <SelectItem
                  key={c}
                  value={c}
                  data-testid={`city-option-${c}`}
                  className="font-ui text-xs"
                >
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeExtraFilters > 0 && (
            <button
              onClick={() => {
                setCityFilter("all");
                setCountryFilter("all");
              }}
              data-testid="clear-location-filters"
              className="border border-[#1E293B] bg-[#080D1A] px-3 py-2 font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5500] hover:border-[#FF5500]"
            >
              Limpiar
            </button>
          )}
        </div>
      </section>

      {/* Content */}
      {error ? (
        <ConnectionError
          error={error}
          onRetry={() => setRetryKey((k) => k + 1)}
        />
      ) : loading ? (
        <div
          data-testid="events-loading"
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-56 animate-pulse border border-[#1E293B] bg-[#0F172A]"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="events-empty"
          className="flex flex-col items-center justify-center gap-4 border border-[#1E293B] bg-[#0F172A] p-16 text-center"
        >
          <Flame className="h-10 w-10 text-[#FF5500]" />
          <h3 className="font-display text-3xl tracking-wider text-white">
            Sin eventos
          </h3>
          <p className="font-body text-sm text-[#94A3B8]">
            No encontramos torneos con los filtros aplicados.
          </p>
        </div>
      ) : (
        <div
          data-testid="events-grid"
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((e, i) => (
            <LiveEventCard
              key={e.id}
              event={e}
              index={i}
              participantCount={getParticipantCount(e)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
